<template>
  <div class="fu-dlg-root">
    <div class="fu-dlg__scrim" data-test="fu-list-dialog-scrim" @click="$emit('close')"></div>
    <div class="fu-dlg__frame">
      <div
        ref="dialog"
        class="fu-dlg"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`${uid}-title`"
        data-test="fu-list-dialog"
        :data-kind="kind"
      >
        <div class="fu-dlg__head">
          <div class="fu-dlg__titles">
            <h2 :id="`${uid}-title`" class="fu-dlg__title">{{ headTitle }}</h2>
            <p v-if="headDescription" class="fu-dlg__desc">{{ headDescription }}</p>
          </div>
          <button type="button" class="fu-dlg__close" aria-label="Close" @click="$emit('close')">
            <fu-icon name="x" :size="18" />
          </button>
        </div>

        <div class="fu-dlg__body">
          <template v-if="kind === 'create'">
            <label class="fu-dlg__label" :for="`${uid}-name`">List name</label>
            <input
              :id="`${uid}-name`"
              v-model="listName"
              type="text"
              class="fu-dlg__input"
              maxlength="200"
              placeholder="e.g. Kids due for recall in October"
              data-test="fu-list-name"
            />
            <label class="fu-dlg__label fu-dlg__label--gap" :for="`${uid}-desc`">
              Description <span class="fu-dlg__optional">· optional</span>
            </label>
            <textarea
              :id="`${uid}-desc`"
              v-model="listDesc"
              rows="2"
              class="fu-dlg__input fu-dlg__textarea"
              placeholder="Who’s on it and why"
              data-test="fu-list-description"
            ></textarea>
          </template>

          <template v-if="kind === 'add'">
            <label class="fu-dlg__label" :for="`${uid}-list`">List</label>
            <select
              :id="`${uid}-list`"
              v-model="listId"
              class="fu-dlg__input fu-dlg__select"
              data-test="fu-list-select"
            >
              <option value="">Choose a list</option>
              <option v-for="l in lists" :key="String(l.id)" :value="String(l.id)">{{ l.name }}</option>
            </select>
          </template>

          <template v-if="kind === 'create' || kind === 'add'">
            <div class="fu-dlg__count" data-test="fu-list-count">{{ patients.length }} {{ patients.length === 1 ? noun : `${noun}s` }}</div>
            <div v-if="conflictText" class="fu-dlg__conflict" role="alert" data-test="fu-list-conflict">{{ conflictText }}</div>
            <div v-if="patients.length" class="fu-dlg__patients">
              <div v-for="p in patients" :key="String(p.id)" class="fu-dlg__patient" data-test="fu-list-patient">
                <span class="fu-dlg__avatar" aria-hidden="true">{{ initials(p.name) }}</span>
                <span class="fu-dlg__pname">{{ p.name }}</span>
                <span v-if="p.onList" class="fu-dlg__onlist">On “{{ p.onList }}”</span>
                <button
                  type="button"
                  class="fu-dlg__remove"
                  :title="`Remove ${p.name} from the selection`"
                  :aria-label="`Remove ${p.name} from the selection`"
                  data-test="fu-list-patient-remove"
                  @click="$emit('remove-patient', p.id)"
                >
                  <fu-icon name="x" :size="16" />
                </button>
              </div>
            </div>
            <div v-else class="fu-dlg__none">Nobody is selected. Close this and tick patients in the table first.</div>
          </template>
        </div>

        <div class="fu-dlg__foot">
          <button type="button" class="fu-dlg__cancel" data-test="fu-list-cancel" @click="$emit('close')">Cancel</button>
          <button
            v-if="kind === 'create' || kind === 'add'"
            type="button"
            class="fu-dlg__save"
            :disabled="saveDisabled"
            data-test="fu-list-save"
            @click="save"
          >{{ busy ? 'Saving…' : 'Save list' }}</button>
          <button
            v-else
            type="button"
            class="fu-dlg__danger"
            :disabled="busy"
            data-test="fu-list-confirm"
            @click="$emit('confirm')"
          >{{ kind === 'delete' ? 'Delete list' : 'Remove' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The list dialogs (the export's lines 1035–1076):
 *
 *   create   "Create a list" — name (required) + optional description, the selected patients.
 *   add      "Add to an existing list" — pick one of `lists`, the selected patients.
 *   delete   confirm deleting the list the page shows (list pages, Phase 3).
 *   remove   confirm taking one patient off the list (list pages, Phase 3).
 *
 * `patients` is [{ id, name, onList }] — `onList` is the name of the list a patient is already on,
 * when the page knows it. A patient can only be on one list at a time, so any such patient shows
 * the warning and keeps Save disabled until they are removed from the selection (the × emits
 * remove-patient with the id; the page drops them from its selection, as the old modal's trash
 * icon did).
 *
 * Emits: save({ kind, name, description, listId }), confirm, remove-patient(id), close.
 * `hide()` (a method, for a parent's $refs) emits close — see below.
 * Pass `title` / `description` to override the headings (delete / remove name the list or patient).
 * `busy` (a save in flight) disables the buttons.
 *
 * A fixed overlay at the follow-ups popover layer — above the app header and the bulk bar, below
 * Bootstrap modals. Escape / the × / Cancel / the dimmed page close it.
 */
import FuIcon from './FuIcon.vue';
import { initialsOf } from '@/utils/followups/rowModel.js';

let uidSeq = 0;

const TITLES = {
  create: ['Create a list', 'Group these patients so anyone on the team can work them together.'],
  add: ['Add to an existing list', 'Pick the list these patients should join.'],
  delete: ['Delete this list?', 'Its patients stay on the page — they just won’t be grouped anymore. Follow-up history isn’t affected.'],
  remove: ['Remove from this list?', 'They’ll still show up on the page with their follow-up history intact.'],
};

export default {
  name: 'FollowupsListDialog',
  components: { FuIcon },
  props: {
    kind: {
      type: String,
      required: true,
      validator: v => ['create', 'add', 'delete', 'remove'].indexOf(v) !== -1,
    },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    lists: { type: Array, default: () => [] },        // [{ id, name }]
    patients: { type: Array, default: () => [] },     // [{ id, name, onList }]
    noun: { type: String, default: 'patient' },
    busy: { type: Boolean, default: false },
  },

  data() {
    uidSeq += 1;
    return {
      uid: `fu-dlg-${uidSeq}`,
      listName: '',
      listDesc: '',
      listId: '',
    };
  },

  computed: {
    headTitle() {
      return this.title || TITLES[this.kind][0];
    },
    headDescription() {
      return this.description || TITLES[this.kind][1];
    },
    conflicts() {
      return this.patients.filter(p => !!p.onList);
    },
    conflictText() {
      const n = this.conflicts.length;
      if (!n) return '';
      return `${n} of these ${this.noun}s ${n > 1 ? 'are' : 'is'} already on a list. A ${this.noun} can only be on one list at a time — remove them to continue.`;
    },
    saveDisabled() {
      if (this.busy || !this.patients.length || this.conflicts.length) return true;
      return this.kind === 'create' ? !this.listName.trim() : !this.listId;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKey, true);
    this.$nextTick(() => {
      const d = this.$refs.dialog;
      const first = d && d.querySelector('.fu-dlg__body input, .fu-dlg__body select, .fu-dlg__foot button');
      if (first && typeof first.focus === 'function') first.focus({ preventScroll: true });
    });
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKey, true);
  },

  methods: {
    initials(name) {
      return initialsOf(name);
    },
    onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        this.$emit('close');
      }
    },
    // The b-modal method a page's EXISTING code calls through a ref (Phase 3): the list pages'
    // removePatient() starts with hideModal('remove-patient-from-list') → $refs[…].hide(). Mounted
    // under that ref, this dialog stands in for the old modal and that code runs unchanged.
    hide() {
      this.$emit('close');
    },
    save() {
      if (this.saveDisabled) return;
      const list = this.lists.find(l => String(l.id) === this.listId);
      this.$emit('save', {
        kind: this.kind,
        name: this.listName.trim(),
        description: this.listDesc.trim(),
        listId: list ? list.id : null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-dlg__scrim {
  @include fu-scrim(true);
  background: rgba(12, 15, 14, 0.42);
}

.fu-dlg__frame {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $fu-z-popover;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $fu-space-4;
  pointer-events: none;
}

.fu-dlg {
  @include fu-font-base;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  width: 520px;
  max-width: 100%;
  max-height: 100%;
  background: $fu-surface;
  border-radius: $fu-radius-xl;
  box-shadow: $fu-shadow-xl;
  animation: fu-dlg-in 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fu-dlg-in {
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: none; }
}

.fu-dlg__head {
  display: flex;
  align-items: flex-start;
  gap: $fu-space-3;
  padding: $fu-space-5 $fu-space-6 $fu-space-2;
}

.fu-dlg__titles {
  flex: 1;
  min-width: 0;
}

.fu-dlg__title {
  @include fu-font-display;
  margin: 0;
  font-weight: 800;
  font-size: 20px;
  letter-spacing: -0.02em;
  color: $fu-text;
  overflow-wrap: anywhere;
}

.fu-dlg__desc {
  margin: 4px 0 0;
  font-size: 13.5px;
  line-height: 1.45;
  color: $fu-text-muted;
}

.fu-dlg__close {
  @include fu-icon-btn(32px);
}

.fu-dlg__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: $fu-space-3 $fu-space-6 0;
}

.fu-dlg__label {
  display: block;
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: $fu-fw-semibold;
}

.fu-dlg__label--gap {
  margin-top: 14px;
}

.fu-dlg__optional {
  font-weight: $fu-fw-medium;
  color: $fu-text-subtle;
}

.fu-dlg__input {
  @include fu-input;
}

.fu-dlg__textarea {
  height: auto;
  padding: 10px 12px;
  resize: vertical;
  font-size: 13.5px;
  @include fu-phone { font-size: 16px; }
}

.fu-dlg__select {
  cursor: pointer;
}

.fu-dlg__count {
  @include fu-eyebrow($fu-text-muted, 12px);
  margin: 18px 0 $fu-space-2;
}

.fu-dlg__conflict {
  margin-bottom: $fu-space-2;
  padding: 10px 12px;
  border-radius: $fu-radius-md;
  background: $fu-warning-subtle;
  border: 1px solid #fbe5bf;
  font-size: 12.5px;
  line-height: 1.45;
  color: #8a5a12;
}

.fu-dlg__patients {
  max-height: 200px;
  overflow: auto;
  border: 1px solid $fu-border;
  border-radius: 11px;
}

.fu-dlg__patient {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: $fu-space-2 10px;
  border-bottom: 1px solid $fu-border;
  &:last-child { border-bottom: 0; }
}

.fu-dlg__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: $fu-brand-100;
  color: $fu-brand-700;
  font-weight: $fu-fw-bold;
  font-size: 10px;
}

.fu-dlg__pname {
  @include fu-ellipsis;
  min-width: 0;
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
}

.fu-dlg__onlist {
  @include fu-pill($fu-warning-subtle, $fu-warning-text);
  font-size: 11.5px;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fu-dlg__remove {
  @include fu-icon-btn(28px);
  margin-left: auto;
  color: $fu-text-subtle;
}

.fu-dlg__none {
  font-size: 13px;
  color: $fu-text-muted;
}

.fu-dlg__foot {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  padding: 18px $fu-space-6 $fu-space-5;
}

.fu-dlg__cancel { @include fu-btn(secondary, md); }
.fu-dlg__save { @include fu-btn(primary, md); }
.fu-dlg__danger { @include fu-btn(danger, md); }

// Phone: a sheet from the bottom; buttons on the LEFT (the agent button owns bottom-right).
@include fu-phone {
  .fu-dlg__frame { align-items: flex-end; padding: 8px; }
  .fu-dlg { width: 100%; border-radius: $fu-radius-lg; }
  .fu-dlg__head { padding: $fu-space-4 $fu-space-4 $fu-space-2; }
  .fu-dlg__body { padding: $fu-space-2 $fu-space-4 0; }
  // row-reverse + flex-end: Save sits at the far LEFT, Cancel beside it.
  .fu-dlg__foot { justify-content: flex-end; flex-direction: row-reverse; padding: 14px $fu-space-4 $fu-space-4; }
  .fu-dlg__select { font-size: 16px; }
}
</style>
